import { useContext } from 'react';
import { useFormaliteService, useSignService } from '@europrocurement/l2d-domain';
import { DossierJsonldReadCollectionTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiSign';
import { getFileExtension } from '@europrocurement/l2d-utils';
import useDownload from '../../../hooks/useDownload';
import { useDocumentSignModalContext } from '../providers/DocumentSignModalContext';
import DocumentPreviewContext from '../providers/DocumentPreviewContext';
import type { DocumentExtension } from './useDossierDocumentFilesApi';
import {
    DossierDocumentSign,
    DossierSignMetadata,
    SignableDocument,
    SignableDocumentRecipient,
    SignablePrescripteur,
} from '../providers/types';

const useDocumentSign = () => {
    const { setDossierId, setPrescripteur, addDocument, setModalOpen, setAllowAddDocument } =
        useDocumentSignModalContext();
    const { dossierApi } = useSignService();
    const { dossierSignApi } = useFormaliteService();
    const { download } = useDownload();
    const { setDocumentPreview } = useContext(DocumentPreviewContext);

    const handleSignSingleDocument = (
        dossierId: number,
        prescripteurId: number,
        prescripteurLabel: string,
        prescripteurEmail: string,
        documentLabel: string,
        fichierId: number,
        fileName?: string,
    ) => {
        setDossierId(dossierId);
        setPrescripteur({ id: prescripteurId, label: prescripteurLabel, email: prescripteurEmail });
        addDocument({ documentName: documentLabel, documentFileName: fileName, fichierId });
        setAllowAddDocument(false);
        setModalOpen(true);
    };

    const handleSignMultiDocuments = (
        dossierId: number,
        prescripteurId: number,
        prescripteurLabel: string,
        prescripteurEmail: string,
    ) => {
        setDossierId(dossierId);
        setPrescripteur({ id: prescripteurId, label: prescripteurLabel, email: prescripteurEmail });
        setModalOpen(true);
        setAllowAddDocument(true);
    };

    const downloadDocument = async (fileName: string, fileId: number): Promise<void> => {
        const response = await dossierSignApi.apiDossierDossiersSignFileIdGet(
            {
                id: fileId as unknown as string,
            },
            { responseType: 'blob' },
        );

        download(fileName, response.data as unknown as Blob);
    };

    const handleShowDocument = async (fileName: string, fileId: number): Promise<void> => {
        const response = await dossierSignApi.apiDossierDossiersSignFileIdGet(
            { id: fileId as unknown as string },
            { responseType: 'blob' },
        );
        const ext = getFileExtension(fileName);
        setDocumentPreview({
            fileName,
            fileType: ext as DocumentExtension,
            content: response.data as unknown as Blob,
        });
    };

    const submitDossier = async (
        dossierId: number,
        subject: string,
        prescripteur: SignablePrescripteur,
        documents: Array<SignableDocument>,
        recipients: Array<SignableDocumentRecipient>,
    ) => {
        await dossierSignApi.apiDossierIdDossiersSignPost({
            id: dossierId as unknown as string,
            dossiersDeSignatureLectroniqueCreateDossierSignJsonldDossierDossierSignWrite: {
                subject,
                prescriberId: prescripteur.id as number,
                documentIds: documents.map((document) => document.fichierId),
                recipients: recipients.map((recipient) => ({
                    nom: recipient.lastName,
                    prenom: recipient.firstName,
                    email: recipient.email,
                    mobile: recipient.mobile,
                })),
            },
        });
    };

    const dossierTransformer = (
        item: DossierJsonldReadCollectionTimestampableBlameambleGroup,
        metadata: Array<DossierSignMetadata>,
    ): DossierDocumentSign => {
        const currentMetadata = metadata.find((m) => m.reference === item.referenceExterne);

        return {
            id: item.id as number,
            reference: item.referenceExterne as string,
            subject: item.sujet as string,
            prescriber: {
                name: item.prescripteur,
                email: item.prescripteurEmail,
            },
            createdBy: {
                name: item.demandeur?.nom,
                reference: item.demandeur?.userIdentifier,
            },
            documents: item.documents.map((document) => {
                const documentMetadata = currentMetadata?.documents.find(
                    (m) => m.externalId === document.id,
                );

                return {
                    id: document.id as unknown as string,
                    signedFileId: documentMetadata?.id,
                    fileId: documentMetadata?.fileId,
                    // Add -signé to the file name ex 'file-sample.pdf' -> 'file-sample-signé.pdf'
                    fileName: documentMetadata?.fileName
                        ? documentMetadata.fileName.replace(/(\.[^.]+)$/, '-signé$1')
                        : undefined,
                    signed: documentMetadata?.signed || false,
                    name: document.nom,
                };
            }),
            recipients: item.signataires.map((recipient) => ({
                firstName: recipient.prenom,
                lastName: recipient.nom,
                email: recipient.email,
                mobile: recipient.mobile,
                link: recipient.lien,
                signed: Boolean(recipient.signature),
                refused: Boolean(recipient.refus),
            })),
            createdAt: item.createdAt ? new Date(item.createdAt) : undefined,
            updatedAt: item.updatedAt ? new Date(item.updatedAt) : undefined,
        } as DossierDocumentSign;
    };

    const dossierSorter = (a: DossierDocumentSign, b: DossierDocumentSign): number => {
        if (!a.createdAt && !b.createdAt) {
            return 0;
        }

        if (!b.createdAt) {
            return -1;
        }

        if (!a.createdAt) {
            return 1;
        }

        return a.createdAt.getTime() < b.createdAt.getTime() ? 1 : -1;
    };

    const getDossierSignMetadata = async (
        dossierId: number,
    ): Promise<Array<DossierSignMetadata>> => {
        const response = await dossierSignApi.apiDossierIdDossiersSignGetCollection({
            id: dossierId as unknown as string,
        });

        return (response.data['hydra:member'] || []).map((item) => ({
            id: item.id,
            reference: item.reference as string,
            documents: (item?.documents || []).map((docItem) => ({
                id: docItem?.id as number,
                fileId: docItem?.fileId as number,
                fileName: docItem?.fileName as string,
                signed: docItem?.signed as boolean,
                externalId: docItem?.externalId ? Number(docItem?.externalId) : 0,
            })),
        }));
    };

    const getDossiers = async (
        metadata: Array<DossierSignMetadata>,
    ): Promise<Array<DossierDocumentSign>> => {
        const references = metadata.map((item) => item.reference as string);

        if (references.length <= 0) {
            return [];
        }

        const request = {
            referenceExterne2: references,
        };

        const response = await dossierApi.getDossierCollection(request);
        const data = response.data['hydra:member'] || [];

        return data.map((item) => dossierTransformer(item, metadata)).sort(dossierSorter);
    };

    const getRaisableDossiers = async (
        metadata: Array<DossierSignMetadata>,
    ): Promise<Array<DossierDocumentSign>> => {
        const references = metadata.map((item) => item.reference as string);

        if (references.length <= 0) {
            return [];
        }

        const request = {
            referenceExterne2: references,
        };

        const response = await dossierApi.toRaiseDossierCollection(request);
        const data = response.data['hydra:member'] || [];

        return data.map((item) => dossierTransformer(item, metadata)).sort(dossierSorter);
    };

    const raiseDossier = async (id: number): Promise<void> => {
        await dossierApi.raiseDossierItem({ id: id as unknown as string });

        return Promise.resolve();
    };

    return {
        handleSignSingleDocument,
        handleSignMultiDocuments,
        getDossierSignMetadata,
        submitDossier,
        getDossiers,
        getRaisableDossiers,
        raiseDossier,
        downloadDocument,
        handleShowDocument,
    };
};

export default useDocumentSign;
